import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { api, API } from '../App';
import { Plus, Pencil, Trash2, Upload, Building2, X } from 'lucide-react';

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Kurumlar() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'ekle' | kurum_obj
  const [form, setForm] = useState({ ad: '', aktif: true });
  const [saving, setSaving] = useState(false);
  const logoRef = useRef();
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/kurumlar'); setList(data); }
    catch { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEkle = () => {
    setForm({ ad: '', aktif: true });
    setLogoFile(null); setLogoPreview(null);
    setModal('ekle');
  };

  const openDuzenle = k => {
    setForm({ ad: k.ad, aktif: k.aktif });
    setLogoFile(null); setLogoPreview(null);
    setModal(k);
  };

  const handleLogoChange = e => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
  };

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      let id;
      if (modal === 'ekle') {
        const { data } = await api.post('/kurumlar', form);
        id = data.id;
        toast.success('Kurum eklendi');
      } else {
        await api.put(`/kurumlar/${modal.id}`, form);
        id = modal.id;
        toast.success('Kurum güncellendi');
      }
      if (logoFile) {
        const fd = new FormData(); fd.append('logo', logoFile);
        await api.post(`/kurumlar/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setModal(null); load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    } finally { setSaving(false); }
  };

  const handleDelete = async k => {
    if (!window.confirm(`"${k.ad}" kurumunu silmek istiyor musunuz?`)) return;
    try { await api.delete(`/kurumlar/${k.id}`); toast.success('Silindi'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Silinemedi'); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Kurumlar</h1>
          <p className="page-subtitle">Sisteme kayıtlı tüm kurumlar</p>
        </div>
        <button className="btn btn-primary" onClick={openEkle}>
          <Plus size={16} /> Yeni Kurum
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-blue" style={{ width: 32, height: 32 }} />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>Kurum Adı</th>
                  <th>Durum</th>
                  <th>Oluşturma</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map(k => (
                  <tr key={k.id}>
                    <td>
                      {k.logo_path
                        ? <img src={`${API.replace('/api','')}/uploads/${k.logo_path}`} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--gray-100)' }} />
                        : <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Building2 size={18} color="var(--gray-400)" />
                          </div>
                      }
                    </td>
                    <td style={{ fontWeight: 600 }}>{k.ad}</td>
                    <td>
                      <span className={`badge ${k.aktif ? 'badge-green' : 'badge-gray'}`}>
                        {k.aktif ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td>{new Date(k.olusturma_tarihi).toLocaleDateString('tr-TR')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openDuzenle(k)}>
                          <Pencil size={14} />
                        </button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(k)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!list.length && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>Henüz kurum yok</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <Modal title={modal === 'ekle' ? 'Yeni Kurum' : 'Kurumu Düzenle'} onClose={() => setModal(null)}>
          <form onSubmit={handleSave}>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="input-group">
                <label className="input-label">Kurum Adı *</label>
                <input className="input" required value={form.ad} onChange={e => setForm({...form, ad: e.target.value})} placeholder="ABC Temizlik A.Ş." />
              </div>

              {/* Logo yükle */}
              <div className="input-group">
                <label className="input-label">Logo</label>
                <div
                  onClick={() => logoRef.current?.click()}
                  style={{
                    border: '2px dashed var(--gray-200)', borderRadius: 12, padding: 20,
                    textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s'
                  }}
                >
                  {logoPreview || (modal !== 'ekle' && modal.logo_path)
                    ? <img
                        src={logoPreview || `${API.replace('/api','')}/uploads/${modal.logo_path}`}
                        alt="" style={{ maxHeight: 80, maxWidth: '100%', borderRadius: 8 }}
                      />
                    : <>
                        <Upload size={24} color="var(--gray-400)" style={{ margin: '0 auto 8px' }} />
                        <p style={{ fontSize: 13, color: 'var(--gray-400)', margin: 0 }}>Logo yüklemek için tıkla</p>
                      </>
                  }
                  <input ref={logoRef} type="file" accept="image/*" hidden onChange={handleLogoChange} />
                </div>
              </div>

              <div className="input-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.aktif} onChange={e => setForm({...form, aktif: e.target.checked})} />
                  <span className="input-label" style={{ margin: 0 }}>Aktif</span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>İptal</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <div className="spinner" /> : null} Kaydet
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
